import type {
  CarpetPricingCatalogItem,
  CarpetPricingCategory,
  BreakdownSubItem,
  BreakdownSubItemInput,
} from '@jobos/shared';
import { CARPET_PRICING_CATEGORIES } from '@jobos/shared';

export interface CarpetRow {
  qty: number;
  discount: number;
}

export interface CarpetUIState {
  tierKey: string | null;
  rowsByKey: Record<string, CarpetRow>;
}

export interface CarpetTotals {
  serviceTotal: number;
  itemsTotal: number;
  surchargesTotal: number;
  discountTotal: number;
  grandTotal: number;
}

export type CatalogByCategory = Record<
  CarpetPricingCategory,
  CarpetPricingCatalogItem[]
>;

export function groupCatalogByCategory(
  items: CarpetPricingCatalogItem[],
): CatalogByCategory {
  const empty = Object.fromEntries(
    CARPET_PRICING_CATEGORIES.map((c) => [c, [] as CarpetPricingCatalogItem[]]),
  ) as CatalogByCategory;

  for (const item of items) {
    empty[item.category].push(item);
  }
  for (const cat of CARPET_PRICING_CATEGORIES) {
    empty[cat].sort((a, b) => a.sortOrder - b.sortOrder);
  }
  return empty;
}

function parseDiscountFromNote(note: string | null | undefined): number {
  if (!note) return 0;
  try {
    const parsed = JSON.parse(note) as { discount?: number };
    return Number(parsed?.discount) || 0;
  } catch {
    return 0;
  }
}

function encodeDiscountNote(discount: number): string | undefined {
  if (!discount || discount <= 0) return undefined;
  return JSON.stringify({ discount });
}

export function readUIFromSubItems(
  subItems: BreakdownSubItem[],
  catalog: CarpetPricingCatalogItem[],
): CarpetUIState {
  const tierKeys = new Set(
    catalog.filter((c) => c.category === 'service_tier').map((c) => c.key),
  );
  const catalogByKey = new Map(catalog.map((c) => [c.key, c]));

  let tierKey: string | null = null;
  const rowsByKey: Record<string, CarpetRow> = {};

  for (const item of subItems) {
    const catalogItem = catalogByKey.get(item.type);
    const isAmountMode = catalogItem?.pricingMode === 'amount';
    const storedAmount = Number((item as { amount?: number | null }).amount);
    const hours = Number((item as { hours?: number | null }).hours) || 0;
    const qty = isAmountMode
      ? (storedAmount > 0 ? 1 : (hours > 0 ? 1 : 0))
      : hours;
    if (qty <= 0) continue;
    const note = (item as { note?: string | null }).note ?? null;
    const discount = parseDiscountFromNote(note);

    if (tierKeys.has(item.type)) {
      tierKey = item.type;
      // tier qty is 1, no UI row
      continue;
    }
    rowsByKey[item.type] = { qty, discount };
  }

  return { tierKey, rowsByKey };
}

export function buildSubItemsFromUI(
  state: CarpetUIState,
  catalog: CarpetPricingCatalogItem[],
): BreakdownSubItemInput[] {
  const byKey = new Map(catalog.map((c) => [c.key, c]));
  const out: BreakdownSubItemInput[] = [];
  let order = 0;

  if (state.tierKey) {
    const tier = byKey.get(state.tierKey);
    if (tier) {
      if (tier.pricingMode === 'amount') {
        out.push({
          type: tier.key,
          amount: Number(tier.defaultRate),
          sortOrder: order++,
        });
      } else {
        out.push({
          type: tier.key,
          hours: 1,
          rate: Number(tier.defaultRate),
          sortOrder: order++,
        });
      }
    }
  }

  for (const [key, row] of Object.entries(state.rowsByKey)) {
    if (!row.qty || row.qty <= 0) continue;
    const item = byKey.get(key);
    if (!item) continue;
    const noteVal = encodeDiscountNote(row.discount);
    const basePayload = {
      type: item.key,
      ...(noteVal !== undefined ? { note: noteVal } : {}),
      sortOrder: order++,
    };
    if (item.pricingMode === 'amount') {
      // Non-service-tier amount-mode items are a deferred UX follow-up per the
      // spec's Open Follow-Ups section. Backend validation guards against
      // inconsistent shapes. Current default: amount = qty * defaultRate.
      out.push({
        ...basePayload,
        amount: row.qty * Number(item.defaultRate),
      });
    } else {
      out.push({
        ...basePayload,
        hours: row.qty,
        rate: Number(item.defaultRate),
      });
    }
  }

  return out;
}

export function computeTotals(
  state: CarpetUIState,
  catalog: CarpetPricingCatalogItem[],
): CarpetTotals {
  const byKey = new Map(catalog.map((c) => [c.key, c]));

  let serviceTotal = 0;
  let itemsTotal = 0;
  let surchargesTotal = 0;
  let discountTotal = 0;

  if (state.tierKey) {
    const tier = byKey.get(state.tierKey);
    if (tier) serviceTotal += Number(tier.defaultRate);
  }

  for (const [key, row] of Object.entries(state.rowsByKey)) {
    const item = byKey.get(key);
    if (!item || !row.qty) continue;
    const lineSub = Number(item.defaultRate) * row.qty;
    const lineDiscount = row.discount || 0;
    discountTotal += lineDiscount;
    if (item.category === 'surcharges') {
      surchargesTotal += lineSub - lineDiscount;
    } else {
      itemsTotal += lineSub - lineDiscount;
    }
  }

  const grandTotal = serviceTotal + itemsTotal + surchargesTotal;
  return { serviceTotal, itemsTotal, surchargesTotal, discountTotal, grandTotal };
}

/**
 * Convert committed sub-items (with db ids) to the input shape used by the
 * save/history pipeline. The `type`, `hours`, `rate`, `note`, and `amount`
 * are preserved; ids and timestamps are dropped.
 */
export function inputsFromSubItems(
  subItems: BreakdownSubItem[],
): BreakdownSubItemInput[] {
  return subItems.map((s, i) => {
    const out: BreakdownSubItemInput = {
      type: s.type,
      sortOrder: s.sortOrder ?? i,
    };
    if (s.hours != null) out.hours = Number(s.hours);
    if (s.rate != null) out.rate = Number(s.rate);
    if ((s as { amount?: unknown }).amount != null) {
      out.amount = Number((s as { amount: number }).amount);
    }
    if (s.note != null) out.note = s.note;
    if (s.markupRate != null) out.markupRate = Number(s.markupRate);
    return out;
  });
}

/**
 * Synthesize display-shape sub-items from inputs so `CarpetEditor` can
 * reseed its internal state (via `readUIFromSubItems`) after undo/redo.
 * Synthetic ids satisfy the BreakdownSubItem discriminated union without
 * touching the database.
 */
export function displayFromInputs(
  inputs: BreakdownSubItemInput[],
): BreakdownSubItem[] {
  const now = new Date().toISOString();
  return inputs.map((s, i) => ({
    id: `history-${i}-${s.type}`,
    lineItemId: 'history',
    type: s.type,
    hours: s.hours ?? 0,
    rate: s.rate ?? 0,
    amount: s.amount ?? null,
    note: s.note ?? null,
    markupRate: s.markupRate ?? null,
    sortOrder: s.sortOrder ?? i,
    createdAt: now,
    updatedAt: now,
  }));
}
